import { create } from "@bufbuild/protobuf";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { MessageInit } from "@/utils/protobuf";
import {
    type Editor,
    type ListEditorsRequest_Filter,
    type EditorVersion,
    ListEditorsRequestSchema,
} from "gitpod-next-api/gitpod/v1/editor_pb";
import { EnvironmentPhase, type Environment } from "gitpod-next-api/gitpod/v1/environment_pb";

export type PlainEditor = Editor;
export type PlainFilter = MessageInit<ListEditorsRequest_Filter>;
export type PlainEditorVersion = EditorVersion;

export const toPlainEditor = (editor: Editor): PlainEditor => {
    return editor;
};

export const editorQueryKey = {
    list: (filter?: PlainFilter) => keyWithPrincipal(["editors", "list", filter]),
    // Prefix to invalidate all editor list queries regardless of filter
    listAll: () => keyWithPrincipal(["editors", "list"]),
    url: (editorId: string, environmentId: string, organizationId: string, workspaceFolder: string) =>
        keyWithPrincipal(["editors", "url", editorId, environmentId, organizationId, workspaceFolder]),
};

/**
 * @deprecated Use useEditorURLQuery instead
 */
export const useEditorURL = () => {
    const api = useGitpodAPI();
    return useMutation({
        mutationFn: ({
            editorId,
            environment,
            version,
        }: {
            editorId: string;
            environment?: Environment;
            version?: string;
        }) => {
            const environmentId = environment?.id;
            const organizationId = environment?.metadata?.organizationId;
            if (!environmentId || !organizationId) {
                throw new Error("No environment or organization ID provided");
            }
            return api.editorService.resolveEditorURL({
                environmentId,
                organizationId,
                editorId,
                version,
            });
        },
    });
};

export const useEditorURLQuery = ({
    editorId,
    environment,
    version,
}: {
    editorId?: string;
    environment?: Environment;
    version?: string;
}) => {
    const api = useGitpodAPI();
    const environmentId = environment?.id;
    const organizationId = environment?.metadata?.organizationId;
    const workspaceFolder = environment?.status?.devcontainer?.remoteWorkspaceFolder;

    // Only resolve the editor URL when the environment is in a phase where
    // the VS Code server can actually be reached. Without this gate the
    // query fires continuously while the environment is stopped, producing
    // a stream of failed requests.
    const phase = environment?.status?.phase;
    const isReachable =
        phase === EnvironmentPhase.RUNNING ||
        phase === EnvironmentPhase.STARTING ||
        phase === EnvironmentPhase.UPDATING;

    return useQuery({
        queryKey: editorQueryKey.url(editorId || "", environmentId || "", organizationId || "", workspaceFolder || ""),
        queryFn: async () => {
            if (!editorId || !environmentId || !organizationId) {
                return undefined;
            }
            return api.editorService.resolveEditorURL({
                environmentId,
                organizationId,
                editorId,
                version,
            });
        },
        // Disable caching
        gcTime: 0,
        staleTime: 0,
        enabled: !!environmentId && !!organizationId && isReachable,
    });
};

export type PlainEditorList = {
    editors: PlainEditor[];
};

export const useListEditors = (filter?: PlainFilter) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: editorQueryKey.list(filter),
        queryFn: async (): Promise<PlainEditorList> => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const editors = await autoPaginate(
                (params) => api.editorService.listEditors(params),
                create(ListEditorsRequestSchema, { filter }),
                (response) => response.editors,
            );

            return {
                editors: editors.map(toPlainEditor),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 60 * 24 * 7, // 1 week
        refetchOnReconnect: "always",
    });
};

/**
 * Gets available versions for a specific editor from the ListEditors API response
 * This uses the versions field returned by the backend, not a separate manifest
 *
 * @param editorId - The ID of the editor to get versions for
 * @param filter - Optional filter to apply (e.g., { allowedByPolicy: true })
 */
export const useEditorVersions = (
    editorId?: string,
    filter?: PlainFilter,
): {
    versions: PlainEditorVersion[];
    hasMultipleVersions: boolean;
    isLoading: boolean;
    error: Error | undefined;
} => {
    const { data: editorList, isLoading, error } = useListEditors(filter);

    return useMemo(() => {
        if (!editorId) {
            return { versions: [], hasMultipleVersions: false, isLoading: false, error: undefined };
        }
        if (isLoading) {
            return { versions: [], hasMultipleVersions: false, isLoading: true, error: undefined };
        }
        if (error) {
            console.error("Failed to fetch editors:", error);
            return { versions: [], hasMultipleVersions: false, isLoading: false, error };
        }
        if (!editorList) {
            return { versions: [], hasMultipleVersions: false, isLoading: false, error: undefined };
        }

        // Find the editor by ID and get its versions
        const editor = editorList.editors.find((e) => e.id === editorId);
        const versions = editor?.versions || [];

        return {
            versions,
            hasMultipleVersions: versions.length > 0,
            isLoading: false,
            error: undefined,
        };
    }, [editorId, editorList, isLoading, error]);
};
